import { useLens } from "@hookform/lenses";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  DialogContent,
  DialogActions,
  MenuItem,
  Typography,
} from "@mui/material";
import { ReactElement, useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";
import z from "zod";

import { FlexBox } from "~frontend/components/FlexBox";
import Select from "~frontend/components/forms/FormSelect";
import {
  RhfErrorBanner,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useErrorMessageFormatter } from "~frontend/util/errors";

import {
  EditIntegrationsList,
  EditIntegrationsListHandle,
} from "./EditIntegrationsList";
import {
  EditTagFormSchema,
  EditTagFormType,
  IntegrationsType,
  NgpVanIntegration,
  TagType,
} from "./types";

export default function EditTagForm({
  onTypeChange,
  initialName,
  initialIntegrations,
  onSave,
  onCancelClick,
  vanIntegrations,
  allOtherTagNames,
}: {
  onTypeChange?: (name: string, newType: "flat" | "singleSelect") => void;
  initialName: string;
  initialIntegrations?: IntegrationsType;
  onSave: (formData: EditTagFormType) => Promise<void>;
  onCancelClick: () => void;
  vanIntegrations: NgpVanIntegration[];
  allOtherTagNames: string[];
}): ReactElement {
  const {
    getValues,
    control,
    formState: { isSubmitting },
    handleSubmit,
    setError,
    subscribe,
  } = useForm({
    resolver: zodResolver(
      EditTagFormSchema.superRefine((data, ctx) => {
        if (data.name && allOtherTagNames.includes(data.name.trim())) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "A tag with this name already exists",
            path: ["name"],
          });
        }

        return true;
      })
    ),
    values: {
      name: initialName,
      integrations: initialIntegrations || [],
    },
  });

  const tagGroupsEnabled = useFeatureFlag("tagGroups");

  const editIntegrationList = useRef<EditIntegrationsListHandle>(null);

  const lens = useLens({ control });

  const integrationsLens = lens.focus("integrations");

  const [hasIntegrations, setHasIntegrations] = useState(false);
  useEffect(() => {
    const callback = subscribe({
      name: "integrations",
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        setHasIntegrations(values.integrations.length > 0);
      },
    });

    return () => callback();
  }, [subscribe]);

  const formatErrorMessage = useErrorMessageFormatter();
  async function handleSave(values: EditTagFormType) {
    try {
      await onSave(values);
    } catch (error) {
      setError("root", { message: formatErrorMessage(error) });
    }
  }

  return (
    <form onSubmit={handleSubmit(handleSave)}>
      <DialogContent>
        <RhfErrorBanner control={control} />
        <RhfTextField
          name="name"
          label="Tag Name"
          control={control}
          fullWidth
        />
        {onTypeChange && tagGroupsEnabled && (
          <Select
            label="Tag Type"
            fullWidth
            value="flat"
            onChange={evt => {
              const newType = evt.target.value as TagType;
              if (newType !== "flat") {
                const name = getValues("name");
                onTypeChange(name as string, newType);
              }
            }}
          >
            <MenuItem value="flat">Flat Tag</MenuItem>
            <MenuItem value="singleSelect">Single Select</MenuItem>
          </Select>
        )}
        <EditIntegrationsList
          lens={integrationsLens}
          vanIntegrations={vanIntegrations}
          ref={editIntegrationList}
        />
        {hasIntegrations && (
          <Typography variant="body1">
            Note: If an admin or texter deletes a tag after applying it to a
            thread, the tag will only be removed from Scale to Win’s data, and
            will not remove the mapped VAN action.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <Button
          variant="contained"
          color="tertiary"
          onClick={() => editIntegrationList.current?.addNewIntegration()}
        >
          + Add a VAN integration
        </Button>
        <FlexBox gap={1}>
          <Button
            onClick={onCancelClick}
            color="tertiary"
            variant="contained"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Save
          </Button>
        </FlexBox>
      </DialogActions>
    </form>
  );
}
